import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import {
  AccountBalance,
  CheckCircleOutline,
  WarningAmberOutlined,
} from '@mui/icons-material';

import {
  IFinTSInstitute,
  useSearchFinTSInstitutes,
} from '../hooks/reactQuerys/FinTSInstituteQueries';
import { useCreateHbciAccount } from '../hooks/reactQuerys/HbciAccountQueries';
import { customFetcher } from '../hooks/useFetch';
import { ILegalClient } from '../Interfaces/ILegalClient';
import { ILegalRole } from '../Interfaces/ILegalRole';
import { useAlert } from '../context/AlertContext';
import { IHbciTanChallenge } from '../Interfaces/IHbciAccount';
import { HbciTanChallengeDialog } from './HbciTanChallengeDialog';

interface Props {
  open: boolean;
  idLegalTransaction: number;
  participants: ILegalClient[];
  /** Mandantenrollen des Vorgangs — für die Sichtbarkeits-Einschränkung. */
  roles?: ILegalRole[];
  onClose: () => void;
  /** Wird nach erfolgreichem Anlegen aufgerufen. Das Argument enthält die
   *  ID des frisch erzeugten ``HbciAccount`` — der Caller kann sie z. B.
   *  direkt im Dropdown vorauswählen. */
  onCreated: (idHbciAccount?: number) => void;
}

interface DiscoveredAccount {
  IBAN: string;
  BIC?: string;
  AccountHolder?: string;
  AccountNumber?: string;
  ProductName?: string;
  AccountType?: string;
  Currency?: string;
  SubAccount?: string;
}

interface DiscoverResult {
  accounts?: DiscoveredAccount[];
  tanRequired?: boolean;
  challenge?: IHbciTanChallenge;
}

const TAN_MECHANISM_OPTIONS = [
  { value: '', label: 'Automatisch (von der Bank wählen lassen)' },
  { value: '942', label: 'mobileTAN / smsTAN (942)' },
  { value: '952', label: 'pushTAN (952) — z. B. Sparkassen-App, ING' },
  { value: '962', label: 'chipTAN-USB (962)' },
  { value: '972', label: 'chipTAN-QR / photoTAN (972)' },
];

const STEPS = ['Bank wählen', 'Anmelden', 'Konto auswählen'] as const;

export const HbciAccountWizardDialog: React.FC<Props> = ({
  open,
  idLegalTransaction,
  participants,
  roles = [],
  onClose,
  onCreated,
}) => {
  const { showAlert } = useAlert();
  const [activeStep, setActiveStep] = useState(0);

  // Schritt 1 — Bank
  // Ein einziges Suchfeld: BLZ, BIC, Name oder Ort funktionieren alle.
  // Der Backend-Search-Endpoint sucht in allen vier Spalten parallel und
  // priorisiert BLZ-Praefix-Treffer.
  const [selectedInstitute, setSelectedInstitute] =
    useState<IFinTSInstitute | null>(null);
  const [bankSearchInput, setBankSearchInput] = useState('');
  // Debounce: nur den eingependelten Wert ans Backend schicken (300 ms),
  // sonst feuern wir bei jedem Tastendruck eine Suche ab.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(bankSearchInput), 300);
    return () => clearTimeout(t);
  }, [bankSearchInput]);
  const { institutes: searchResults, isSearchingInstitutes } =
    useSearchFinTSInstitutes(debouncedSearch);

  // Schritt 2 — Login
  const [login, setLogin] = useState('');
  const [pin, setPin] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [tanMechanism, setTanMechanism] = useState('');
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [discoverError, setDiscoverError] = useState<string | null>(null);
  const [tanChallenge, setTanChallenge] = useState<IHbciTanChallenge | null>(
    null
  );

  // Schritt 3 — Konto
  const [discoveredAccounts, setDiscoveredAccounts] = useState<
    DiscoveredAccount[]
  >([]);
  const [selectedIban, setSelectedIban] = useState<string>('');
  const [selectedClient, setSelectedClient] = useState<number | ''>('');
  // Sichtbarkeit: leer = alle Mandanten; sonst nur diese Rollen.
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);

  const { createHbciAccount, isPendingCreateHbciAccount } =
    useCreateHbciAccount();

  const isDsgv =
    selectedInstitute?.Organisation?.toUpperCase().includes('DSGV') ?? false;

  const resetWizard = () => {
    setActiveStep(0);
    setSelectedInstitute(null);
    setBankSearchInput('');
    setDebouncedSearch('');
    setLogin('');
    setPin('');
    setCustomerId('');
    setTanMechanism('');
    setIsDiscovering(false);
    setDiscoverError(null);
    setTanChallenge(null);
    setDiscoveredAccounts([]);
    setSelectedIban('');
    setSelectedClient('');
    setSelectedRoleIds([]);
  };

  const handleClose = () => {
    if (isDiscovering || isPendingCreateHbciAccount) return;
    resetWizard();
    onClose();
  };

  const handleDiscover = async () => {
    if (!selectedInstitute || !login.trim() || !pin.trim()) {
      setDiscoverError('Bitte Login und PIN vollständig eintragen.');
      return;
    }
    setIsDiscovering(true);
    setDiscoverError(null);
    try {
      const result = (await customFetcher({
        endpoint: '/hbciaccount/discover',
        method: 'POST',
        body: {
          HbciUrl: selectedInstitute.HbciUrl,
          HbciBlz: selectedInstitute.Blz,
          HbciLogin: login,
          HbciPin: pin,
          HbciCustomerId: customerId || undefined,
          HbciTanMechanism: tanMechanism || undefined,
        },
      })) as DiscoverResult & { error?: string };

      if (result.error) {
        setDiscoverError(result.error);
        return;
      }
      if (result.tanRequired && result.challenge) {
        setTanChallenge(result.challenge);
        // Hinweis: für den Discover-Pfad ist der TAN-Flow im Backend
        // derzeit als "erst Konto manuell anlegen, dann TAN durchspielen"
        // ausgelegt. Hier ist die Challenge nur eine Info, die wir dem
        // User direkt zeigen.
        return;
      }
      const accounts = result.accounts ?? [];
      if (accounts.length === 0) {
        setDiscoverError('Bank meldet keine Konten unter diesem Login.');
        return;
      }
      setDiscoveredAccounts(accounts);
      setSelectedIban(accounts[0].IBAN);
      setActiveStep(2);
    } catch (e) {
      // Beim 500/Netzwerk-Fehler oder einem unerwarteten Exception-Pfad im
      // Backend (z. B. abgewiesene Logindaten, die nicht als sauberer
      // ``hbciError`` zurueckkommen) reicht der rohe Fehlertext fuer den
      // Notar nicht — ``HTTPError 500`` oder ein Stacktrace verwirrt mehr
      // als es hilft. Wir matchen daher auf bekannte Muster und zeigen
      // eine freundliche, handlungsorientierte Meldung.
      const raw = e instanceof Error ? e.message : String(e ?? '');
      const lower = raw.toLowerCase();
      let friendly: string;
      if (
        lower.includes('500') ||
        lower.includes('internal server') ||
        lower.includes('refusing to use pin') ||
        lower.includes('pin') ||
        lower.includes('login') ||
        lower.includes('credentials')
      ) {
        friendly =
          'Die Anmeldedaten konnten von der Bank nicht geprüft werden. ' +
          'Bitte überprüfen Sie Bankleitzahl, Anmeldename und PIN — und ' +
          'stellen Sie sicher, dass der FinTS-Zugang im Online-Banking ' +
          'aktiv ist.';
      } else if (lower.includes('timeout') || lower.includes('econn')) {
        friendly =
          'Die Bank antwortet derzeit nicht. Bitte gleich noch einmal ' +
          'versuchen — wenn das Problem anhält, prüfen Sie die Internet- ' +
          'oder Bank-Server-Verfügbarkeit.';
      } else {
        friendly =
          'Anmeldung bei der Bank fehlgeschlagen. Bitte Eingaben prüfen ' +
          'und erneut versuchen.';
      }
      setDiscoverError(friendly);
    } finally {
      setIsDiscovering(false);
    }
  };

  const handleSave = async () => {
    if (!selectedInstitute || !selectedIban) return;
    const account = discoveredAccounts.find(a => a.IBAN === selectedIban);
    if (!account) return;
    try {
      const created = (await createHbciAccount({
        IBAN: account.IBAN,
        LegalTransaction_idLegalTransaction: idLegalTransaction,
        BIC: account.BIC ?? selectedInstitute.Bic ?? '',
        BankName: selectedInstitute.Institut,
        AccountHolder: account.AccountHolder ?? '',
        LegalClient_idLegalClient:
          typeof selectedClient === 'number' ? selectedClient : undefined,
        LegalRoleIds: selectedRoleIds,
        HbciUrl: selectedInstitute.HbciUrl ?? '',
        HbciBlz: selectedInstitute.Blz,
        HbciLogin: login,
        HbciCustomerId: customerId || undefined,
        HbciTanMechanism: tanMechanism || undefined,
        HbciPin: pin,
      })) as { idHbciAccount?: number } | undefined;
      showAlert({
        text: `Notaranderkonto „${account.IBAN}" angelegt.`,
        severity: 'success',
      });
      resetWizard();
      onCreated(created?.idHbciAccount);
    } catch (e) {
      showAlert({
        text:
          e instanceof Error
            ? `Anlegen fehlgeschlagen: ${e.message}`
            : 'Anlegen fehlgeschlagen.',
        severity: 'error',
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      disableEscapeKeyDown={isDiscovering || isPendingCreateHbciAccount}
    >
      <DialogTitle>
        <Stack direction='row' spacing={1} alignItems='center'>
          <AccountBalance color='primary' />
          <Typography variant='h6'>Notaranderkonto hinzufügen</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {STEPS.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Schritt 1 — Bank wählen */}
        {activeStep === 0 && (
          <Stack spacing={2}>
            <Typography variant='body2' color='text.secondary'>
              Suchen Sie nach Ihrer Bank — entweder per Bankleitzahl, BIC,
              Bankname oder Ort. DiNo greift auf den offiziellen ZKA-Katalog
              zurück und hinterlegt die richtige FinTS-URL automatisch.
            </Typography>
            <Autocomplete<IFinTSInstitute, false, false, false>
              autoHighlight
              fullWidth
              filterOptions={x => x}
              options={searchResults}
              loading={isSearchingInstitutes}
              value={selectedInstitute}
              onChange={(_e, value) => setSelectedInstitute(value)}
              inputValue={bankSearchInput}
              onInputChange={(_e, value, reason) => {
                if (reason === 'reset') return;
                setBankSearchInput(value);
              }}
              getOptionLabel={opt =>
                `${opt.Institut}${opt.Ort ? `, ${opt.Ort}` : ''} (${opt.Blz})`
              }
              isOptionEqualToValue={(o, v) =>
                o.idFinTSInstitute === v.idFinTSInstitute
              }
              noOptionsText={
                bankSearchInput.trim().length < 2
                  ? 'Mindestens 2 Zeichen eingeben …'
                  : 'Keine Bank gefunden'
              }
              renderOption={(props, opt) => (
                <li {...props} key={opt.idFinTSInstitute}>
                  <Stack spacing={0.25} sx={{ width: '100%' }}>
                    <Stack
                      direction='row'
                      spacing={1}
                      alignItems='center'
                      justifyContent='space-between'
                    >
                      <Typography variant='body2'>
                        <strong>{opt.Institut}</strong>
                        {opt.Ort ? `, ${opt.Ort}` : ''}
                      </Typography>
                      <Chip
                        size='small'
                        variant='outlined'
                        label={`BLZ ${opt.Blz}`}
                      />
                    </Stack>
                    <Typography variant='caption' color='text.secondary'>
                      {opt.Bic ? `BIC ${opt.Bic} · ` : ''}
                      {opt.Organisation || '–'}
                    </Typography>
                  </Stack>
                </li>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  autoFocus
                  label='Bank suchen (BLZ, BIC, Name oder Ort)'
                  helperText='z. B. „ING", „50010517", „INGDDEFFXXX" oder „Berliner Sparkasse"'
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isSearchingInstitutes ? (
                          <CircularProgress color='inherit' size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {selectedInstitute && !isDsgv && (
              <Alert
                severity='success'
                icon={<CheckCircleOutline fontSize='inherit' />}
              >
                <strong>{selectedInstitute.Institut}</strong>
                {selectedInstitute.Ort ? `, ${selectedInstitute.Ort}` : ''}
                <br />
                <Typography variant='caption'>
                  FinTS {selectedInstitute.Version || 'V3.0'} ·{' '}
                  {selectedInstitute.Organisation || '–'} ·{' '}
                  {selectedInstitute.HbciUrl}
                </Typography>
              </Alert>
            )}

            {selectedInstitute && isDsgv && (
              <Alert severity='warning' icon={<WarningAmberOutlined />}>
                <AlertTitle>
                  {selectedInstitute.Institut} gehört zum Deutsche Sparkassen-
                  und Giroverband (DSGV)
                </AlertTitle>
                Banken des DSGV-Dachverbands werden derzeit noch nicht
                unterstützt. Durch die Aktivierung der FinAPI- oder
                DATEV-Erweiterung können Sie künftig auf diese Banken zugreifen.
                (BaFin-AISP-Zulassung für XS2A-API notwendig).
              </Alert>
            )}
          </Stack>
        )}

        {/* Schritt 2 — Anmelden */}
        {activeStep === 1 && selectedInstitute && (
          <Stack spacing={2}>
            <Typography variant='body2' color='text.secondary'>
              Anmeldedaten für <strong>{selectedInstitute.Institut}</strong>.
              Die PIN wird verschlüsselt gespeichert und nie zurück ans Frontend
              gesendet. Bei TAN-pflichtigen Banken (PSD2) öffnet sich
              automatisch der TAN-Dialog.
            </Typography>
            <TextField
              label='Legitimationsnummer / Anmeldename'
              value={login}
              onChange={e => setLogin(e.target.value)}
              helperText='Bei den meisten Banken die Online-Banking-Anmeldung; bei manchen Sparkassen die Legitimations-ID (16-stellig).'
              fullWidth
              autoFocus
            />
            <TextField
              label='PIN'
              type='password'
              value={pin}
              onChange={e => setPin(e.target.value)}
              helperText='Identisch mit der Online-Banking-PIN.'
              fullWidth
              autoComplete='new-password'
            />
            <TextField
              label='Kunden-ID (optional)'
              value={customerId}
              onChange={e => setCustomerId(e.target.value)}
              helperText='Nur wenn die Bank eine separate Kunden-Nummer verlangt (i. d. R. leer lassen).'
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>TAN-Verfahren (optional)</InputLabel>
              <Select
                label='TAN-Verfahren (optional)'
                value={tanMechanism}
                onChange={e => setTanMechanism(e.target.value)}
              >
                {TAN_MECHANISM_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {discoverError && <Alert severity='error'>{discoverError}</Alert>}
            {tanChallenge && (
              <Alert severity='info'>
                <AlertTitle>TAN während Konto-Suche erforderlich</AlertTitle>
                Die Bank verlangt eine TAN-Bestätigung, bevor die Konten
                aufgelistet werden können. Erstellen Sie das Konto erst mit
                bekannter IBAN, durchlaufen Sie die TAN-Bestätigung beim ersten
                Saldo-Abruf und nutzen Sie den Wizard für weitere Konten danach
                erneut.
              </Alert>
            )}
          </Stack>
        )}

        {/* Schritt 3 — Konto auswählen */}
        {activeStep === 2 && (
          <Stack spacing={2}>
            <Typography variant='body2' color='text.secondary'>
              Die Bank hat folgende Konten unter dem angegebenen Login
              zurückgegeben. Wählen Sie das Notaranderkonto.
            </Typography>
            <FormControl>
              <RadioGroup
                value={selectedIban}
                onChange={e => setSelectedIban(e.target.value)}
              >
                {discoveredAccounts.map(a => {
                  // ProductName (z. B. „Girokonto", „Sparkonto Sonstiges") prominent
                  // in der ersten Zeile zeigen — der Notar erkennt das Konto
                  // damit ohne die IBAN entziffern zu muessen. IBAN
                  // monospace darunter, Inhaber als Tertiaer-Info.
                  const productLabel =
                    a.ProductName?.trim() || a.AccountType?.trim() || '';
                  return (
                    <FormControlLabel
                      key={a.IBAN}
                      value={a.IBAN}
                      control={<Radio size='small' />}
                      sx={{ alignItems: 'flex-start', mt: 0.5 }}
                      label={
                        <Stack spacing={0.25} sx={{ py: 0.5 }}>
                          <Stack
                            direction='row'
                            spacing={1}
                            alignItems='center'
                          >
                            <Typography variant='body1'>
                              <strong>{productLabel || 'Konto'}</strong>
                            </Typography>
                            {a.Currency && a.Currency !== 'EUR' && (
                              <Chip
                                size='small'
                                label={a.Currency}
                                variant='outlined'
                              />
                            )}
                          </Stack>
                          <Typography
                            variant='body2'
                            fontFamily='monospace'
                            color='text.secondary'
                          >
                            {a.IBAN}
                          </Typography>
                          {a.AccountHolder && (
                            <Typography
                              variant='caption'
                              color='text.secondary'
                            >
                              Inhaber: {a.AccountHolder}
                            </Typography>
                          )}
                        </Stack>
                      }
                    />
                  );
                })}
              </RadioGroup>
            </FormControl>

            {participants.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>Mandant (optional)</InputLabel>
                <Select
                  label='Mandant (optional)'
                  value={selectedClient}
                  onChange={e =>
                    setSelectedClient(
                      e.target.value === '' ? '' : Number(e.target.value)
                    )
                  }
                >
                  <MenuItem value=''>– keine Verknüpfung –</MenuItem>
                  {participants.map(p => (
                    <MenuItem key={p.idLegalClient} value={p.idLegalClient}>
                      {[p.FirstName, p.LastName].filter(Boolean).join(' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {roles.length > 0 && (
              <FormControl fullWidth>
                <InputLabel id='hbci-role-visibility-label'>
                  Sichtbar für Rolle(n)
                </InputLabel>
                <Select<number[]>
                  labelId='hbci-role-visibility-label'
                  multiple
                  value={selectedRoleIds}
                  onChange={e =>
                    setSelectedRoleIds(
                      (typeof e.target.value === 'string'
                        ? e.target.value.split(',').map(Number)
                        : e.target.value) as number[]
                    )
                  }
                  input={<OutlinedInput label='Sichtbar für Rolle(n)' />}
                  renderValue={selected => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as number[]).map(id => {
                        const r = roles.find(role => role.idLegalRole === id);
                        return (
                          <Chip
                            key={id}
                            size='small'
                            label={r?.LegalRole ?? `#${id}`}
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {roles.map(r => (
                    <MenuItem key={r.idLegalRole} value={r.idLegalRole}>
                      <Checkbox
                        checked={selectedRoleIds.indexOf(r.idLegalRole) > -1}
                      />
                      <ListItemText primary={r.LegalRole} />
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Leer lassen = für alle Mandanten des Vorgangs sichtbar. Sonst
                  sehen nur Mandanten mit einer der gewählten Rollen (z. B. nur
                  Käufer) das Konto im Mandantenportal.
                </FormHelperText>
              </FormControl>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={isDiscovering || isPendingCreateHbciAccount}
        >
          Abbrechen
        </Button>
        {activeStep > 0 && (
          <Button
            onClick={() => setActiveStep(s => s - 1)}
            disabled={isDiscovering || isPendingCreateHbciAccount}
          >
            Zurück
          </Button>
        )}
        {activeStep === 0 && (
          <Button
            variant='contained'
            onClick={() => setActiveStep(1)}
            disabled={!selectedInstitute || isDsgv}
          >
            Weiter
          </Button>
        )}
        {activeStep === 1 && (
          <Button
            variant='contained'
            onClick={handleDiscover}
            disabled={!login.trim() || !pin.trim() || isDiscovering}
            startIcon={
              isDiscovering ? <CircularProgress size={16} /> : undefined
            }
          >
            {isDiscovering ? 'Verbinde …' : 'Anmelden & Konten suchen'}
          </Button>
        )}
        {activeStep === 2 && (
          <Button
            variant='contained'
            onClick={handleSave}
            disabled={!selectedIban || isPendingCreateHbciAccount}
            startIcon={
              isPendingCreateHbciAccount ? (
                <CircularProgress size={16} />
              ) : undefined
            }
          >
            {isPendingCreateHbciAccount ? 'Speichere …' : 'Konto speichern'}
          </Button>
        )}
      </DialogActions>

      <HbciTanChallengeDialog
        open={tanChallenge !== null}
        idLegalTransaction={idLegalTransaction}
        idHbciAccount={0}
        challenge={tanChallenge}
        onResolved={() => setTanChallenge(null)}
        onError={msg => showAlert({ text: msg, severity: 'error' })}
      />
    </Dialog>
  );
};
